import { SimpleQuestionnaire, useProgress } from "@smartintegrity/questionnaire";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import { RiRobotLine } from "react-icons/ri";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import { mapAnswersToSave } from "src/modules/data-protection/helpers/questionnaire";
import AssessmentPanel from "../../../components/AssessmentPanel";
import AssessmentActions, {
    submitQuestionnaireForm
} from "../../../components/AssessmentActions";
import AssessmentSaveButton from "../../../components/AssessmentSaveButton";
import useComplianceModule, {
    COMPLIANCE_MODULE_SLUGS
} from "../../../hooks/useComplianceModule";
import { mapRiskAssessmentSummary } from "../../../helpers/summaryCards";
import { createQuestionnaireAiSuggestionExtension } from "../../../components/QuestionnaireAiSuggestionExtension";
import { createQuestionnaireAssessmentGapExtension } from "../../../components/QuestionnaireAssessmentGapExtension";
import { composeQuestionExtensions } from "../../../components/composeQuestionExtensions";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{width: '100%', height: '55px'}} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        </div>
    );
}

const CompleteWithAIConfirmationModal = ({
    t,
    isOpen,
    toggle,
    onConfirm,
    isLoading = false
}) => {
    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="md"
        >
            <ModalHeader toggle={toggle}>
                {t('Complete Risk Assessment with AI')}
            </ModalHeader>

            <ModalBody>
                <div className="text-center">
                    <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                        style={{
                            width: 72,
                            height: 72,
                            background: "rgba(13, 110, 253, 0.1)"
                        }}
                    >
                        <RiRobotLine
                            size={36}
                            className="text-primary"
                        />
                    </div>

                    <h5 className="mb-3">
                        {t('Generate AI Suggestions')}
                    </h5>

                    <p className="text-muted mb-0">
                        {t('Would you like to complete the risk assessment using AI suggestions?')}
                    </p>

                    <small className="text-muted d-block mt-3">
                        {t('AI will analyze the available information and generate responses for the risk assessment questionnaire. The generated responses will be applied automatically and can be reviewed or updated later.')}
                    </small>
                </div>
            </ModalBody>

            <ModalFooter>
                <Button
                    color="light"
                    onClick={toggle}
                    disabled={isLoading}
                >
                    {t('Cancel')}
                </Button>

                <Button
                    color="primary"
                    onClick={onConfirm}
                    disabled={isLoading}
                >
                    {isLoading
                        ? t("Generating...")
                        : t("Generate with AI")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const RiskAssessmentQuestionnaire = ({
    questionsQuery,
    t,
    token,
    softwareId,
    onValidSubmit,
    onProgressChange
}) => {
    const [isCreateAIOpen, setIsCreateAIOpen] = useState(false);
    const [, setQuestionnaireCompleted] = useState(false);
    const questionnaireRef = useRef();
    const summaryQuery = useComplianceModule(
        softwareId,
        COMPLIANCE_MODULE_SLUGS.riskAssessment,
        { t }
    );
    const riskSummary = useMemo(() => {
        return mapRiskAssessmentSummary(
            summaryQuery.isError ? null : summaryQuery.data?.summary
        );
    }, [summaryQuery.data, summaryQuery.isError]);

    const save = useCallback(() => {
        submitQuestionnaireForm(questionnaireRef.current);
    }, []);

    const {
        control: controlQuestionnaireProgress,
        register: registerQuestionnaireForProgress
    } = useProgress();

    const updateQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(
                `${API_BASE_URL}/dpia/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            )

            return response.data;
        },
        onSuccess({
            data
        }) {
            toast(t("Report Submitted"), {
                type: 'success'
            });

            onValidSubmit && onValidSubmit(data);
            summaryQuery.refetch();
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Error!'), {
                type: "error"
            })
        }
    });

    const completeWithAIMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                `${API_BASE_URL}/dpia/ai_generate`,
                {
                    softwareId: Number(softwareId)
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            return response.data;
        },
        onSuccess: () => {
            questionsQuery.refetch();
            summaryQuery.refetch();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t("An error occurred while generating by AI."), {
                type: "error"
            });
        },
        onSettled: () => {
            setIsCreateAIOpen(false);
        }
    });

    const questionnaireStatusChangeCallback = useCallback((_currentPayload, newStatus) => {
        setQuestionnaireCompleted(newStatus)
    }, []);

    const handleSubmitQuestionnaire = useCallback((payload) => {
        updateQuestionnaireMutation.mutate({
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                payload
            ),
            software: softwareId,
            questionnaireType: 'risk_assessment',
            hardware: null
        })
    }, [
        questionsQuery.data?.rawQuestions,
        updateQuestionnaireMutation,
        softwareId
    ]);

    const settings = useMemo(() => {
        return {
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ''
            },
            /**
             * Save is rendered below the panel, outside the library's form.
             */
            buttonsSettings: {
                submit: {
                    display: false
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return `${API_BASE_URL}/report.uploadAttachment`
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [
        token
    ]);

    useEffect(() => {
        registerQuestionnaireForProgress('risk-assessment-questionnaire');
    }, [ ]);

    const questionnaireTabsProgressState = controlQuestionnaireProgress.progressQuestionnaires.find(q => q.id === 'risk-assessment-questionnaire');

    const requiredQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.required || 0);
    }, 0) || 0;
    const requiredAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.requiredAnswered || 0);
    }, 0) || 0;

    const optionalQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optional || 0);
    }, 0) || 0;
    const optionalAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optionalAnswered || 0);
    }, 0) || 0;


    const answeredQuestionsCount =
        requiredAnsweredQuestionsCount + optionalAnsweredQuestionsCount;
    const questionsCount = requiredQuestions + optionalQuestions;

    useEffect(() => {
        // Counters are empty until the questionnaire is registered; reporting
        // them would reset the navigation item to 0%.
        if (!questionsCount) {
            return;
        }

        onProgressChange && onProgressChange({
            answered: answeredQuestionsCount,
            total: questionsCount,
            percent: Math.round(
                (answeredQuestionsCount / questionsCount) * 100
            )
        });
    }, [
        answeredQuestionsCount,
        questionsCount,
        onProgressChange
    ]);

    const aiAssistantButton = (
        <>
            <CompleteWithAIConfirmationModal
                isOpen={isCreateAIOpen}
                toggle={() => setIsCreateAIOpen(false)}
                onConfirm={completeWithAIMutation.mutate}
                isLoading={completeWithAIMutation.isLoading}
                t={t}
            />

            <div className="assessment-panel__assistant-card">
                <Button
                    color="primary"
                    outline
                    type="button"
                    onClick={() => setIsCreateAIOpen(true)}
                >
                    <i className="ri-sparkling-line me-1" />
                    {t("AI Assessment Assistant")}
                </Button>
                <p className="mb-0">
                    {t(
                        "Get a plain-language explanation of your answers and risk indicators."
                    )}
                </p>
            </div>
        </>
    );

    const questionnaireComponents = useMemo(() => {
        const QuestionExtension = composeQuestionExtensions(
            createQuestionnaireAiSuggestionExtension(),
            createQuestionnaireAssessmentGapExtension({
                distinguishRiskConditions: true
            })
        );

        return QuestionExtension ? { QuestionExtension } : undefined;
    }, []);

    const questionnaire = (
        <SimpleQuestionnaire
            questionnaireUniqueId='risk-assessment-questionnaire'
            className='gdpr'
            questions={questionsQuery.data?.mappedQuestions || []}
            defaultValues={questionsQuery.data?.mappedAnswers || []}
            settings={settings}
            components={questionnaireComponents}
            onQuestionnaireCompleteStatusChanged={questionnaireStatusChangeCallback}
            onSubmit={handleSubmitQuestionnaire}
            {...controlQuestionnaireProgress}
        />
    );

    if (questionsQuery.isFetching) {
        return (
            <AssessmentPanel
                id="risk-assessment"
                title={t("Risk Assessment")}
                subtitle={t("A privacy pre-screen to identify whether the processing is likely to result in a significant risk to data subjects' legal interests.")}
                status={riskSummary.status ? {
                    ...riskSummary.status,
                    label: t(riskSummary.status.label),
                    description: riskSummary.status.description
                        ? t(riskSummary.status.description)
                        : undefined
                } : null}
                summaries={riskSummary.cards}
                summariesLoading={summaryQuery.isFetching && !summaryQuery.data}
            >
                <QuestionsLoadingPlaceholder />
            </AssessmentPanel>
        )
    }

    return (
        <AssessmentPanel
            id="risk-assessment"
            title={t("Risk Assessment")}
            subtitle={t("A privacy pre-screen to identify whether the processing is likely to result in a significant risk to data subjects' legal interests.")}
            status={riskSummary.status ? {
                ...riskSummary.status,
                label: t(riskSummary.status.label),
                description: riskSummary.status.description
                    ? t(riskSummary.status.description)
                    : undefined
            } : null}
            toolbar={aiAssistantButton}
            summaries={riskSummary.cards}
            summariesLoading={summaryQuery.isFetching && !summaryQuery.data}
        >
            <div
                className="assessment-panel__questionnaire"
                ref={questionnaireRef}
            >
                {questionnaire}
            </div>

            <AssessmentActions>
                <AssessmentSaveButton
                    isSaving={updateQuestionnaireMutation.isLoading}
                    onClick={save}
                />
            </AssessmentActions>
        </AssessmentPanel>
    );
}

export default withNamespaces()(
    connect((state) => {
        const { token, user } = state.Login;

        return {
            token,
            user
        };
    })(
        RiskAssessmentQuestionnaire
    )
);